import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import SliderStarsAsLabels from '../Sliders/StarsAsLabels/SliderStarsAsLabels';
import React, { ChangeEvent, Dispatch, FC, SetStateAction } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { CommunityManagement } from '../../shared/interfaces/ICommunityManagement';
import { constants } from '../../constants/constants';

interface Props {
  settings: CommunityManagement;
  onChange: Dispatch<SetStateAction<CommunityManagement>>;
}

const CommunityManagementConfigurator: FC<Props> = ({ settings, onChange }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const handlePreferredLanguageSelectChange = (event: SelectChangeEvent) => {
    onChange((prevState) => ({
      ...prevState,
      languagePreference: event.target.value as Language,
    }));
  };

  const handleScoreThresholdChange = (event: Event, newValue: number | number[]) => {
    onChange((prevState) => ({
      ...prevState,
      scoreThreshold: newValue as number,
    }));
  };

  const handleTonalitySelectChange = (event: SelectChangeEvent) => {
    onChange((prevState) => ({
      ...prevState,
      tonality: {
        ...prevState.tonality,
        preference: parseInt(event.target.value),
      },
    }));
  };

  const handlePronounSelectChange = (event: SelectChangeEvent) => {
    onChange((prevState) => ({
      ...prevState,
      pronoun: {
        ...prevState.pronoun,
        preference: parseInt(event.target.value),
      },
    }));
  };

  const handleSalutationPrefixChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange((prevState) => ({
      ...prevState,
      salutationPrefix: event.target.value,
    }));
  };

  const handleUseUsernameSwitchChange = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    onChange((prevState) => ({
      ...prevState,
      useUsername: checked,
    }));
  };

  const handleGreetingChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange((prevState) => ({
      ...prevState,
      greeting: event.target.value,
    }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: theme.spacing(2) }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700 }}
      >
        {t('community_management_setup')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', mt: theme.spacing(2) }}>
        <FormControl
          size="small"
          sx={{ mt: theme.spacing(2) }}
        >
          <InputLabel id="preferred-language-select-label">{t('preferred_language')}</InputLabel>
          <Select
            labelId="preferred-language-select-label"
            value={settings.languagePreference}
            onChange={handlePreferredLanguageSelectChange}
            label={t('preferred_language')}
          >
            <MenuItem value="de">{t('german')}</MenuItem>
            <MenuItem value="en">{t('english')}</MenuItem>
            <MenuItem value="nl">{t('dutch')}</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', mt: theme.spacing(2) }}>
          <Typography
            variant="body2"
            sx={{ mr: theme.spacing(1) }}
          >
            {t('score')}
          </Typography>
          <Tooltip
            title={t('community_management_score_tooltip')}
            placement="right-end"
            arrow
          >
            <InfoOutlined
              fontSize="small"
              sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
            />
          </Tooltip>
        </Box>
        <SliderStarsAsLabels
          value={settings.scoreThreshold}
          onChange={handleScoreThresholdChange}
          sx={{ mt: theme.spacing(1) }}
        />
        <FormControl
          size="small"
          sx={{ mt: theme.spacing(2) }}
        >
          <InputLabel id="tonality-select-label">{t('tonality')}</InputLabel>
          <Select
            labelId="tonality-select-label"
            value={settings.tonality.preference.toString()}
            onChange={handleTonalitySelectChange}
            label={t('tonality')}
          >
            {constants.communityManagement.tonalityOptions.map((tonality, index) => {
              return (
                <MenuItem
                  key={index}
                  value={index}
                >
                  <Trans t={t}>{tonality}</Trans>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        {settings.languagePreference !== 'en' && (
          <FormControl
            size="small"
            sx={{ mt: theme.spacing(2) }}
          >
            <InputLabel id="pronoun-select-label">{t('pronoun')}</InputLabel>
            <Select
              labelId="pronoun-select-label"
              value={settings.pronoun.preference.toString()}
              onChange={handlePronounSelectChange}
              label={t('pronoun')}
            >
              {constants.communityManagement.pronounOptions.map((pronoun, index) => {
                return (
                  <MenuItem
                    key={index}
                    value={index}
                  >
                    <Trans t={t}>{pronoun}</Trans>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            fullWidth
            label={t('salutation')}
            variant="outlined"
            size="small"
            value={settings.salutationPrefix}
            placeholder={settings.salutationPrefix}
            onChange={handleSalutationPrefixChange}
            sx={{ mt: theme.spacing(2) }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.useUsername}
                onChange={handleUseUsernameSwitchChange}
              />
            }
            label={<Typography variant="body2">{t('use_username')}</Typography>}
            sx={{ mt: theme.spacing(1) }}
          />
          <TextField
            fullWidth
            multiline
            label={t('greeting')}
            variant="outlined"
            size="small"
            value={settings.greeting}
            placeholder={settings.greeting}
            onChange={handleGreetingChange}
            sx={{ mt: theme.spacing(2) }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CommunityManagementConfigurator;
